// File: frontend/src/components/common/AnimatedNumericDisplay.tsx
import React from 'react';
import CountUp from 'react-countup';
import { useScoreChangeHighlight } from '@/hooks/useScoreChangeHighlight';

// Rolling digits are the canonical trigger for motion sensitivity; users who
// set Reduce Motion get the static value while KEEPING the color flash and
// diff chip (non-motion feedback). Same doctrine as Spinner/CoachMark's
// motion-reduce classes. Read once: OS-level setting, session-stable.
const REDUCED_MOTION =
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

interface AnimatedScoreProps {
  score: number;
  previousScore?: number | null;
  /** Digit classes (size/weight). Default preserves the DFS scorecard look. */
  digitClassName?: string;
  /** Color when not flashing a change. */
  neutralClassName?: string;
  wrapperClassName?: string;
  /** The inline "+2.5" chip next to the digits. Turn off where space is tight
      or the container is centered (the chip mounting shifts layout). */
  showDiff?: boolean;
}

export const AnimatedScore: React.FC<AnimatedScoreProps> = ({
  score,
  previousScore,
  digitClassName = 'font-bold text-lg',
  neutralClassName = 'text-accent-yellow',
  wrapperClassName = 'flex items-baseline justify-end gap-1 font-mono',
  showDiff = true,
}) => {
  const { isHighlight, diff, prevScore } = useScoreChangeHighlight(score);
  const hasChanged = isHighlight && diff !== 0;
  const hasIncreased = hasChanged && diff > 0;
  const hasDecreased = hasChanged && diff < 0;

  // Roll from the caller-supplied previous value when provided (the DFS pages
  // keep prev snapshots); otherwise from the hook's captured baseline, which
  // is stable for the whole highlight window so the CountUp key doesn't churn
  // mid-roll. When there's nothing to roll (or Reduce Motion is set), render
  // plain text: countup.js runs its full-duration rAF loop even for
  // start === end, and dozens of idle cells paying that on every mount is
  // pure main-thread waste.
  const startValue = previousScore !== null && previousScore !== undefined
    ? previousScore
    : prevScore;
  const digitClasses = `${digitClassName} transition-colors duration-300 ${
    hasIncreased ? 'text-green-400' : hasDecreased ? 'text-red-400' : neutralClassName
  }`;

  return (
    <div className={wrapperClassName}>
      {!REDUCED_MOTION && startValue !== score ? (
        <CountUp
          start={startValue}
          end={score}
          duration={1.2}
          decimals={1}
          separator=","
          useEasing={true}
          // Force re-animation when score changes
          key={`${startValue}-${score}`}
          className={digitClasses}
        />
      ) : (
        <span className={digitClasses}>{score.toFixed(1)}</span>
      )}

      {showDiff && hasChanged && (
        <span className={`text-xs font-semibold animate-fade-in ${
          hasIncreased ? 'text-green-400' : 'text-red-400'
        }`}>
          {diff > 0 ? '+' : ''}{diff.toFixed(1)}
        </span>
      )}
    </div>
  );
};

interface AnimatedRankProps {
  rank: number;
  previousRank?: number | null;
}

export const AnimatedRank: React.FC<AnimatedRankProps> = ({ rank, previousRank }) => {
  // minDelta 1: ranks are integers; sub-integer noise can't exist.
  const { isHighlight, diff, prevScore } = useScoreChangeHighlight(rank, 1);
  // Rank moving DOWN numerically is an improvement (positive change).
  const rankChange = -diff;
  const hasChanged = isHighlight && rankChange !== 0;

  const startValue = previousRank !== null && previousRank !== undefined
    ? previousRank
    : prevScore;
  const digitClasses = `font-bold transition-colors duration-300 ${
    hasChanged ? 'text-white' : 'text-gray-400'
  }`;

  return (
    <div className="flex items-center justify-start gap-2">
      {!REDUCED_MOTION && startValue !== rank ? (
        <CountUp
          start={startValue}
          end={rank}
          duration={0.8}
          separator=""
          prefix="#"
          useEasing={true}
          // Force re-animation when rank changes
          key={`${startValue}-${rank}`}
          className={digitClasses}
        />
      ) : (
        <span className={digitClasses}>#{rank}</span>
      )}

      {hasChanged && (
        <span className={`text-sm flex items-center font-bold animate-fade-in ${
          rankChange > 0 ? 'text-green-400' : 'text-red-400'
        }`}>
          {rankChange > 0 ? '↑' : '↓'}{Math.abs(rankChange)}
        </span>
      )}
    </div>
  );
};
